import React, { useEffect, useRef } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

  :root {
    --ink: #0f0e0d;
    --ink-muted: #5a5650;
    --ink-faint: #b8b2aa;
    --paper: #faf8f4;
    --paper-warm: #f2ede4;
    --accent: #c8392b;
    --accent-light: #f5e8e6;
    --rule: #ddd8cf;
    --serif: 'Playfair Display', Georgia, serif;
    --sans: 'DM Sans', system-ui, sans-serif;
  }

  .blog-root * { box-sizing: border-box; margin: 0; padding: 0; }
  .blog-root { font-family: var(--sans); background: var(--paper); color: var(--ink); min-height: 100vh; }

  /* ── Header Bar ── */
  .blog-header {
    border-bottom: 1px solid var(--rule);
    padding: 14px 0;
    background: var(--paper);
    position: sticky; top: 0; z-index: 50;
  }
  .blog-header-inner {
    max-width: 1200px; margin: 0 auto; padding: 0 32px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .blog-header-pub {
    font-family: var(--serif); font-size: 18px; font-weight: 700; letter-spacing: -0.01em;
  }
  .blog-header-tag {
    font-size: 11px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--ink-muted); background: var(--paper-warm);
    border: 1px solid var(--rule); border-radius: 2px; padding: 4px 10px;
  }

  /* ── Hero ── */
  .blog-hero { max-width: 1200px; margin: 0 auto; padding: 64px 32px 0; }

  .blog-kicker {
    display: flex; align-items: center; gap: 12px; margin-bottom: 28px;
  }
  .blog-kicker-line { flex: 0 0 32px; height: 2px; background: var(--accent); }
  .blog-kicker-text {
    font-size: 11px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--accent);
  }

  .blog-title {
    font-family: var(--serif); font-size: clamp(36px, 5.5vw, 72px);
    font-weight: 900; line-height: 1.05; letter-spacing: -0.02em;
    color: var(--ink); max-width: 900px;
  }
  .blog-title em { font-style: italic; color: var(--accent); }

  .blog-deck {
    margin-top: 24px; font-size: 18px; font-weight: 300; line-height: 1.65;
    color: var(--ink-muted); max-width: 600px; border-left: 2px solid var(--rule);
    padding-left: 20px;
  }

  /* ── Hero Image ── */
  .blog-hero-image {
    margin-top: 48px; position: relative; overflow: hidden;
    aspect-ratio: 16 / 7; background: var(--paper-warm);
  }
  .blog-hero-image img {
    width: 100%; height: 100%; object-fit: cover;
    display: block; transition: transform 8s ease;
  }
  .blog-hero-image:hover img { transform: scale(1.03); }
  .blog-hero-caption {
    position: absolute; bottom: 16px; right: 16px;
    font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase;
    color: rgba(255,255,255,0.7); background: rgba(0,0,0,0.4);
    padding: 4px 10px; border-radius: 2px;
  }

  /* ── Divider ── */
  .blog-divider {
    max-width: 1200px; margin: 0 auto; padding: 0 32px;
    display: flex; align-items: center; gap: 24px; margin-top: 56px;
  }
  .blog-divider hr { flex: 1; border: none; border-top: 1px solid var(--rule); }
  .blog-divider-ornament { font-family: var(--serif); font-size: 20px; color: var(--ink-faint); }

  /* ── Body Layout ── */
  .blog-body {
    max-width: 1200px; margin: 0 auto; padding: 0 32px 96px;
    display: grid; grid-template-columns: 280px 1fr;
    gap: 0 80px; margin-top: 56px;
    align-items: start;
  }

  /* ── Sidebar ── */
  .blog-sidebar { position: sticky; top: 80px; }

  .blog-sidebar-lead {
    font-family: var(--serif); font-size: 22px; font-weight: 700;
    line-height: 1.35; letter-spacing: -0.01em; color: var(--ink);
    margin-bottom: 24px; padding-bottom: 24px; border-bottom: 1px solid var(--rule);
  }

  .blog-meta-row {
    display: flex; flex-direction: column; gap: 14px; margin-bottom: 32px;
  }
  .blog-meta-item { font-size: 12px; color: var(--ink-muted); }
  .blog-meta-label {
    display: block; font-weight: 500; letter-spacing: 0.1em;
    text-transform: uppercase; font-size: 10px; color: var(--ink-faint); margin-bottom: 3px;
  }

  .blog-toc { margin-top: 32px; }
  .blog-toc-title {
    font-size: 10px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase;
    color: var(--ink-faint); margin-bottom: 14px;
  }
  .blog-toc-list { list-style: none; display: flex; flex-direction: column; gap: 8px; }
  .blog-toc-item a {
    font-size: 13px; color: var(--ink-muted); text-decoration: none;
    display: flex; align-items: center; gap: 8px;
    transition: color 0.15s;
  }
  .blog-toc-item a:hover { color: var(--accent); }
  .blog-toc-item a::before {
    content: ''; flex: 0 0 16px; height: 1px; background: currentColor; opacity: 0.4;
  }

  /* ── Article ── */
  .blog-article { min-width: 0; }

  .blog-article h2 {
    font-family: var(--serif); font-size: 30px; font-weight: 700;
    line-height: 1.2; letter-spacing: -0.01em; color: var(--ink);
    margin-top: 56px; margin-bottom: 20px; padding-top: 56px;
    border-top: 1px solid var(--rule);
  }
  .blog-article h2:first-child { margin-top: 0; padding-top: 0; border-top: none; }

  .blog-article h3 {
    font-family: var(--serif); font-size: 18px; font-weight: 700; font-style: italic;
    color: var(--ink); margin-top: 28px; margin-bottom: 10px;
  }

  .blog-article p {
    font-size: 17px; line-height: 1.78; color: #2a2724; margin-bottom: 20px;
  }

  .blog-article strong {
    font-weight: 600; color: var(--ink);
  }

  /* Impact statement */
  .blog-impact {
    font-family: var(--serif); font-size: 20px; font-style: italic;
    line-height: 1.5; color: var(--ink); background: var(--paper-warm);
    border-left: 3px solid var(--accent); padding: 20px 24px;
    margin: 28px 0;
  }

  /* Lists */
  .blog-article ul {
    margin: 0 0 20px 0; padding: 0; list-style: none;
    display: flex; flex-direction: column; gap: 8px;
  }
  .blog-article ul li {
    font-size: 16px; line-height: 1.6; color: #2a2724;
    display: flex; align-items: flex-start; gap: 12px;
  }
  .blog-article ul li::before {
    content: '—'; color: var(--accent); font-weight: 700;
    flex-shrink: 0; margin-top: 1px;
  }

  /* Blockquote */
  .blog-article blockquote {
    margin: 40px 0; padding: 32px 40px;
    background: var(--ink); color: var(--paper);
    position: relative;
  }
  .blog-article blockquote::before {
    content: '\u201C';
    font-family: var(--serif); font-size: 80px; line-height: 0.8;
    color: var(--accent); position: absolute; top: 24px; left: 24px;
    opacity: 0.6;
  }
  .blog-article blockquote p {
    font-family: var(--serif); font-size: 21px; font-style: italic;
    line-height: 1.5; color: var(--paper); margin: 0; padding-left: 32px;
  }

  /* Inline image */
  .blog-article .article-image {
    margin: 40px -24px; overflow: hidden;
  }
  .blog-article .article-image img {
    width: 100%; display: block; aspect-ratio: 16 / 7; object-fit: cover;
  }

  /* Impact cards for Cost/Revenue/Risk */
  .blog-cards {
    display: grid; grid-template-columns: 1fr 1fr 1fr;
    gap: 1px; background: var(--rule);
    border: 1px solid var(--rule); margin: 28px 0 36px;
  }
  .blog-card {
    background: var(--paper); padding: 24px 20px;
  }
  .blog-card-label {
    font-size: 10px; font-weight: 500; letter-spacing: 0.14em; text-transform: uppercase;
    color: var(--accent); margin-bottom: 14px; display: flex; align-items: center; gap: 8px;
  }
  .blog-card-label::after { content: ''; flex: 1; height: 1px; background: var(--accent-light); }
  .blog-card ul { margin: 0; }
  .blog-card ul li { font-size: 13px; line-height: 1.5; color: var(--ink-muted); }

  /* ── Footer ── */
  .blog-footer {
    border-top: 1px solid var(--rule); background: var(--paper-warm);
    padding: 40px 32px; text-align: center;
  }
  .blog-footer p {
    font-size: 13px; color: var(--ink-faint);
    font-family: var(--sans);
  }
  .blog-footer strong { color: var(--ink-muted); }

  /* Responsive */
  @media (max-width: 900px) {
    .blog-body { grid-template-columns: 1fr; gap: 40px; }
    .blog-sidebar { position: static; }
    .blog-toc { display: none; }
    .blog-cards { grid-template-columns: 1fr; }
    .blog-article .article-image { margin: 32px 0; }
    .blog-hero { padding: 40px 20px 0; }
    .blog-body { padding: 0 20px 64px; margin-top: 40px; }
    .blog-divider { padding: 0 20px; }
  }
`;

export default function BlogPart0() {
  const imgRef = useRef(null);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.style.opacity = "1"; },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{styles}</style>
      <div className="blog-root">

        {/* ── Sticky Header ── */}
        {/* <header className="blog-header">
          <div className="blog-header-inner">
            <span className="blog-header-pub">Decision Integrity</span>
            <span className="blog-header-tag">Foundation Series · Vol. 1</span>
          </div>
        </header> */}

        {/* ── Hero ── */}
        <div className="blog-hero">
          <div className="blog-kicker">
            <span className="blog-kicker-line" />
            <span className="blog-kicker-text">Decision Integrity — Foundation</span>
          </div>

          <h1 className="blog-title">
            What Decision Integrity <em>Really</em> Means — and Why It Impacts Cost, Revenue, and Risk
          </h1>

          <p className="blog-deck">
            Most systems are optimized to act fast. Very few are designed to ask whether the decision should be made at all.
          </p>

          <div className="blog-hero-image" ref={imgRef} style={{ opacity: 1 }}>
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1600&auto=format&fit=crop"
              alt="Data systems and decision making"
            />
            <span className="blog-hero-caption">Data · Systems · Decisions</span>
          </div>
        </div>

        {/* ── Ornamental Divider ── */}
        <div className="blog-divider">
          <hr />
          <span className="blog-divider-ornament">§</span>
          <hr />
        </div>

        {/* ── Body: Sidebar + Article ── */}
        <div className="blog-body">

          {/* Left Sidebar */}
          <aside className="blog-sidebar">
            <p className="blog-sidebar-lead">
              "If data exists → take action." In real-world systems, this rule silently breaks things.
            </p>

            <div className="blog-meta-row">
              <div className="blog-meta-item">
                <span className="blog-meta-label">Series</span>
                Decision Integrity
              </div>
              <div className="blog-meta-item">
                <span className="blog-meta-label">Reading Time</span>
                5 min
              </div>
              <div className="blog-meta-item">
                <span className="blog-meta-label">Topic</span>
                Systems · Risk · Operations
              </div>
            </div>

            <nav className="blog-toc">
              <p className="blog-toc-title">In this article</p>
              <ul className="blog-toc-list">
                {["What is Decision Integrity?", "Where things break", "How this affects your business", "The shift", "Why this matters now"].map(t => (
                  <li key={t} className="blog-toc-item"><a href="#">{t}</a></li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Main Article */}
          <article className="blog-article">

            <h2>What is Decision Integrity?</h2>
            <p>
              Decision Integrity means ensuring that a system only executes a decision when the underlying data and conditions are strong enough to support it.
            </p>
            <p>Most systems today follow a deceptively simple rule:</p>
            <p className="blog-impact">If data exists → take action.</p>
            <p>
              But in real-world systems, data is often incomplete, delayed, or inconsistent. Decisions made on top of that state may look correct — but fail under scrutiny.
            </p>

            <div className="article-image">
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1600&auto=format&fit=crop"
                alt="Data validation and system alignment"
              />
            </div>

            <h2>Where things break</h2>
            <p>Most business systems are connected across multiple sources:</p>
            <ul>
              <li>Internal systems</li>
              <li>External data feeds</li>
              <li>Manual updates</li>
              <li>Delayed signals</li>
            </ul>
            <p>These systems rarely align perfectly in time or accuracy. Yet decisions are still executed as if they do.</p>

            <blockquote>
              <p>The issue is not bad data. The issue is acting before the system state is reliable.</p>
            </blockquote>

            <h2>How this affects your business</h2>
            <p>
              The impact is rarely dramatic in a single instance. It shows up gradually — across cost, revenue, and risk.
            </p>

            <div className="blog-cards">
              <div className="blog-card">
                <div className="blog-card-label">Cost</div>
                <ul>
                  <li>Time spent resolving inconsistencies</li>
                  <li>Manual reconciliation across systems</li>
                  <li>Operational inefficiencies from exceptions</li>
                </ul>
              </div>
              <div className="blog-card">
                <div className="blog-card-label">Revenue</div>
                <ul>
                  <li>Decisions that don't fully convert</li>
                  <li>Conservative behavior from low confidence</li>
                  <li>Inconsistent outcomes across similar scenarios</li>
                </ul>
              </div>
              <div className="blog-card">
                <div className="blog-card-label">Risk</div>
                <ul>
                  <li>Hidden inconsistencies across systems</li>
                  <li>Decisions made on partial information</li>
                  <li>Exposure when decisions are questioned later</li>
                </ul>
              </div>
            </div>

            <div className="article-image">
              <img
                src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=1600&auto=format&fit=crop"
                alt="Risk analysis and business decisions"
              />
            </div>

            <h2>The shift</h2>
            <p>Decision Integrity introduces a simple but powerful change:</p>
            <p className="blog-impact">your business systems should execute decisions which are defensible.</p>
            <p>This means:</p>
            <ul>
              <li>Validating inputs before acting</li>
              <li>Detecting inconsistencies across systems</li>
              <li>Holding or delaying weak decisions</li>
              <li>Executing only when conditions are reliable</li>
            </ul>

            <blockquote>
              <p>Most systems optimize for speed. Decision Integrity optimizes for outcomes that hold up.</p>
            </blockquote>

            <h2>Why this matters now</h2>
            <p>
              As systems become more automated and AI-driven, decisions are happening faster and at greater scale.
            </p>
            <p>
              Without a control layer, small inconsistencies can propagate into larger financial, operational, or risk issues.
            </p>
            <p>
              Decision Integrity acts as that control layer — not to slow systems down, but to ensure that what they do actually holds up in the real world.
            </p>

          </article>
        </div>

        {/* ── Footer ── */}
        <footer className="blog-footer">
         <p><strong style={{ color: "var(--ink-muted)" }}>Documore LLC</strong> — © {new Date().getFullYear()}</p>
        </footer>

      </div>
    </>
  );
}
